import { ProductPriceRangeFragment } from '@graphql/fragments/__generated__/ProductPriceRange.generated'
import PlaceHolder from 'images/placeholder-img.svg'
import React, { MouseEventHandler, useMemo, useState } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Link } from 'react-router-dom'
import { getProductUrl } from '../../urls'
import FavoritesBtnView from './FavoritesBtn/FavoritesBtnView'
import ProductDetailsDialog from './ProductDetailsDialog'
import { PromoCodeFragment } from '@graphql/fragments/__generated__/PromoCode.generated'
import FormattedPrice from '@components/FormattedPrice'
import _ from 'lodash'
import { useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Swiper, SwiperSlide } from 'swiper/react'
import { SwiperOptions } from 'swiper'
import { ProductReflectionsQueryResult } from '@graphql/queries/__generated__/reflection.generated'

export const ProductPrice: React.FC<{
  priceRange: ProductPriceRangeFragment
}> = ({ priceRange }) => {
  const price_min = priceRange.discount.min
  const price_max = priceRange.discount.max
  const undiscount_price_min = priceRange.undiscount.min
  const undiscount_price_max = priceRange.undiscount.max
  const currency = priceRange.currency
  const isPriceRange = price_min !== price_max
  const isSale = undiscount_price_min !== price_min || undiscount_price_max !== price_max
  return (
    <p className='price' itemProp='offers' itemType='https://schema.org/Offer'>
      <span className='flex flex-col'>
        <span className={isSale ? 'line-through' : ''}>
          {!isPriceRange && (
            <span className='currency data-hj-allow'>
              <FormattedPrice currency={currency} amount={undiscount_price_min} />
            </span>
          )}
          {isPriceRange && (
            <span className='currency data-hj-allow'>
              <FormattedPrice currency={currency} amount={undiscount_price_min} /> -{' '}
              <FormattedPrice currency={currency} amount={undiscount_price_max} />
            </span>
          )}
        </span>
        {isSale && (
          <span className='text-red'>
            {!isPriceRange && (
              <span className='currency data-hj-allow'>
                <FormattedPrice currency={currency} amount={price_min} />
              </span>
            )}
            {isPriceRange && (
              <span className='currency data-hj-allow'>
                <FormattedPrice currency={currency} amount={price_min} /> -{' '}
                <FormattedPrice currency={currency} amount={price_max} />
              </span>
            )}
          </span>
        )}
      </span>
    </p>
  )
}

interface ReflectionCardProps {
  spec: ProductReflectionsQueryResult['data']['specifics']['edges'][0]['node']

  onGooglePushDataLayer(spec, name: string, index: number, type: string): void

  showBigImg: boolean
  idx: number
  showFavoritesButton?: boolean
}

const ReflectionCard: React.FC<ReflectionCardProps> = ({
  spec,
  onGooglePushDataLayer,
  showBigImg,
  idx,
  showFavoritesButton,
}) => {
  const theme = useTheme()
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'))
  const [activeProductData, setActiveProductData] = useState(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const product = spec.product

  const specUrl = getProductUrl(spec.slug, spec.pk)
  const handleClickLink = (event: any, pk: number, slug: string) => {
    event.preventDefault()
    onGooglePushDataLayer(spec, spec.categories?.[0]?.name, idx + 1, 'Category Results')
    setActiveProductData({ pk, slug })
  }

  let slides = [
    {
      image: product?.thumbnail.lg || PlaceHolder,
      smallImage: product?.thumbnail.sm || PlaceHolder,
      link: getProductUrl(spec.slug, spec.pk),
      pk: spec.pk,
      slug: spec.slug,
      name: spec.shortName || spec.name,
      priceRange: product?.priceRange,
    },
  ]

  if (spec.product.similarSpecifics?.length) {
    slides.push(
      ...spec.product.similarSpecifics.map(item => ({
        image: item.thumbnail.lg,
        smallImage: item.thumbnail.sm,
        link: getProductUrl(item.specifics[0]?.slug, item.specifics[0]?.pk),
        pk: item.specifics[0].pk,
        slug: item.specifics[0]?.slug,
        name: item.specifics[0]?.shortName || item.specifics[0]?.name,
        priceRange: item.priceRange,
      }))
    )
  } else if (product?.variantImages.length) {
    slides = product?.variantImages.map(item => ({
      image: item.thumbnail.lg,
      smallImage: item.thumbnail.sm,
      link: getProductUrl(spec.slug, spec.pk),
      pk: spec.pk,
      slug: spec.slug,
      name: spec.shortName || spec.name,
      priceRange: product?.priceRange,
    }))
  }

  const uniqSlides = _.uniqBy(slides, 'image')

  const swiperOptions: SwiperOptions = useMemo(
    () => ({
      spaceBetween: 0,
      slidesPerView: 1,
      lazy: false,
      preloadImages: true,
      pagination: {
        clickable: true,
        el: `#swiper-pagination-${idx}`,
      },
      effect: lgDown ? 'slide' : 'fade',
      speed: lgDown ? 250 : 0,
      watchSlidesProgress: true,
      autoHeight: false,
      touchMoveStopPropagation: true,
      preventClicksPropagation: true,
      watchOverflow: false,
    }),
    [JSON.stringify(uniqSlides), idx, lgDown]
  )

  const handleMouseMove = swiper => e => {
    if (!swiper || lgDown) return

    const rect = swiper.el.getBoundingClientRect()

    const x = e.clientX - rect.left
    const percent = x / rect.width

    const index = Math.max(Math.floor(percent * swiper.slides.length), 0)
    swiper.slideTo(index)
  }

  const handleMouseLeave = swiper => () => {
    if (!swiper || lgDown) return
    swiper.slideTo(0)
  }

  return (
    <>
      <div>
        <Swiper
          key={lgDown ? 'mobile' : 'desktop'}
          onSwiper={swiper => {
            swiper.el.addEventListener('mousemove', handleMouseMove(swiper))
            swiper.el.addEventListener('mouseleave', handleMouseLeave(swiper))
          }}
          onSlideChange={swiper => {
            setActiveSlideIndex(swiper.realIndex)
          }}
          {...swiperOptions}
        >
          {uniqSlides.map(slide => (
            <SwiperSlide key={slide.image} className='!w-full'>
              <div
                className={`relative bg-white ${product?.isOutOfStock ? 'opacity-50' : ''}`}
                style={{ paddingBottom: '138%' }}
              >
                <Link
                  to={slide.link}
                  onClick={e => handleClickLink(e, slide.pk, slide.slug)}
                  aria-label='product'
                  style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}
                >
                  <LazyLoadImage
                    alt={spec.shortName}
                    effect='opacity'
                    placeholderSrc={PlaceHolder}
                    aria-label={slide.smallImage}
                    src={(showBigImg ? slide.image : slide.smallImage) || PlaceHolder}
                    visibleByDefault
                  />
                </Link>
                {showFavoritesButton && product && (
                  <FavoritesBtnView isInFavorites={product.isInFavorites} pk={product.pk} />
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          id={`swiper-pagination-${idx}`}
          className={`swiper-pagination swiper-pagination-clickable min-h-20 h-20 swiper-pagination-bullets w-full mt-5 !pb-0 ${
            uniqSlides.length > 0 ? '!block' : 'hidden'
          }`}
        />
      </div>
      <div className={`${product?.isOutOfStock ? 'opacity-50' : ''}`}>
        <div className='caption'>
          <h4 itemProp='name' className='!mt-0'>
            <Link
              aria-label='product'
              className='animsition-link line-clamp-3'
              onClick={e => handleClickLink(e, spec.pk, spec.slug)}
              to={specUrl}
              itemProp='url'
            >
              {slides[activeSlideIndex]?.name || spec.shortName || spec.name}
            </Link>
          </h4>
          <ProductPrice priceRange={slides[activeSlideIndex]?.priceRange || product?.priceRange} />
        </div>
        {product?.isOutOfStock && (
          <span className='font-bold text-white bg-red px-10 py-5 rounded-full'>Out of Stock</span>
        )}
      </div>
      <ProductDetailsDialog isOpen={!!activeProductData} setIsOpen={setActiveProductData} spec={activeProductData} />
    </>
  )
}

export default ReflectionCard
